import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { FC } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { Alignment } from '@app/shared/enums/alignment.enum';
import TableSpacerRow from '@app/shared/components/table/TableSpacerRow';
import TableSectionValuesPerCase from '../../components/TableSectionValuesPerCase';
import { useLocale } from '@app/core/hooks/useLocale';
import {
  getWaterfallCaseValue,
  getWaterfallCaseYearValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';
import useIsErfProject from '@app/shared/hooks/useIsErfProject';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import SummaryRow from './SummaryRow';

interface TotalEquitySectionProps {
  columnsCount: number;
}

const TotalEquitySection: FC<TotalEquitySectionProps> = ({ columnsCount }) => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const isErfProject = useIsErfProject();
  const { l } = useLocale();

  return (
    <>
      <tr data-testid="total-equity-header-row">
        <th className="table-primary__cell--header">{l('_TotalEquity')}</th>
        <th colSpan={columnsCount - 1} className="table-primary__cell--header" />
      </tr>
      <TableSectionValuesPerForecast
        title={isErfProject ? l('_PVOfTotalEquityCumulativeInterimAndExit') : l('_PVOfTotalEquity')}
        renderSpacer={false}
        renderCell={({ caseItem, forecast }) => (
          <CellValue
            {...amountRules}
            value={getWaterfallCaseYearValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              (year) =>
                isErfProject
                  ? year.cumulativePresentValueOfTotalEquity
                  : year.presentValueOfTotalEquity
            )}
            dataTestId={`present-value-total-equity-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerCase
        title={l('_CaseMVOfTotalEquity')}
        renderSpacer={false}
        renderCell={({ caseItem }) => (
          <CellValue
            {...amountRules}
            alignment={Alignment.Center}
            value={getWaterfallCaseValue(
              results,
              caseItem.caseId,
              (caseItem) => caseItem.weightedTotalEquityFairValue
            )}
            dataTestId={`case-mv-of-total-equity-cell-${caseItem.caseId}`}
          />
        )}
        additionalTitleClassNames="table-primary__cell--strong"
        additionalCellClassNames="table-primary__cell--strong"
      />
      <SummaryRow
        label={l('_MVOfTotalEquityAllCases')}
        columnsCount={columnsCount}
        cellValueProps={{
          ...amountRules,
          alignment: Alignment.Center,
          value: results.waterfall.weightedTotalEquityFairValue,
          dataTestId: 'mv-of-total-equity-all-cases-cell',
        }}
        dataTestId="mv-of-total-equity-all-cases"
        isStrong
        isSectionStart
      />
      <SummaryRow
        label={l('_TotalEquityInvestment')}
        columnsCount={columnsCount}
        cellValueProps={{
          ...amountRules,
          alignment: Alignment.Center,
          value: results.waterfall.totalInvestment,
          dataTestId: 'total-equity-investment-cell',
        }}
        dataTestId="total-equity-investment"
        isStrong
      />
      <SummaryRow
        label={l('_PcVarianceToTotalEquityValue')}
        columnsCount={columnsCount}
        cellValueProps={{
          ...enteredPercentageViewRules,
          alignment: Alignment.Center,
          value: getCalculatedProjectValueInPercent(results.waterfall.totalEquityFairValueVariance),
          dataTestId: 'pc-variance-to-current-market-value-cell',
        }}
        dataTestId="pc-variance-to-current-market-value"
      />
      <TableSpacerRow />
      <TableSpacerRow />
    </>
  );
};

export default TotalEquitySection;
